import { useEffect } from 'react';

const HomeSEO = () => {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://gwdevelopers.com';

    document.title = 'Web Development Nashville TN | GWDevelopers AI Web Design';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Nashville web development agency using AI to build fast, professional websites for small businesses. Serving Nashville, Franklin, Brentwood & East Nashville. Get a free demo!'
      );
    }
    const canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonical) canonical.href = `${siteUrl}/`;

    const schemas = [
      {
        id: 'home-website-schema',
        data: {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'GWDevelopers',
          url: siteUrl,
          description:
            'AI-powered web development in Nashville, TN. GWDevelopers builds fast, modern websites for small businesses across Nashville and Middle Tennessee.',
          potentialAction: {
            '@type': 'SearchAction',
            target: `${siteUrl}/?s={search_term_string}`,
            'query-input': 'required name=search_term_string',
          },
        },
      },
      {
        id: 'home-organization-schema',
        data: {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          '@id': `${siteUrl}/#organization`,
          name: 'GWDevelopers',
          url: siteUrl,
          logo: {
            '@type': 'ImageObject',
            url: 'https://static.readdy.ai/image/8054a7de0b572fc3037c5f8704bad458/843a78c9ac2e1ca22b2f04d54313adc9.png',
            width: 512,
            height: 512,
          },
          foundingDate: '2023',
          founder: {
            '@type': 'Person',
            name: 'Josh Gee',
          },
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: '740-641-7292',
            email: 'info@gwdevelopers.com',
            contactType: 'customer service',
            areaServed: 'US',
            availableLanguage: 'English',
          },
          sameAs: [],
        },
      },
      {
        id: 'home-business-schema',
        data: {
          '@context': 'https://schema.org',
          '@type': 'ProfessionalService',
          '@id': `${siteUrl}/#localbusiness`,
          name: 'GWDevelopers',
          description:
            'GWDevelopers is a Nashville, TN web development agency that uses AI to build professional websites for small businesses quickly and affordably.',
          url: siteUrl,
          telephone: '740-641-7292',
          email: 'info@gwdevelopers.com',
          priceRange: '$$',
          image: 'https://static.readdy.ai/image/8054a7de0b572fc3037c5f8704bad458/843a78c9ac2e1ca22b2f04d54313adc9.png',
          founder: {
            '@type': 'Person',
            name: 'Josh Gee',
          },
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Nashville',
            addressRegion: 'TN',
            postalCode: '37201',
            addressCountry: 'US',
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 36.1627,
            longitude: -86.7816,
          },
          hasMap: 'https://maps.google.com/?q=Nashville,TN',
          areaServed: [
            { '@type': 'City', name: 'Nashville', containedInPlace: { '@type': 'State', name: 'Tennessee' } },
            { '@type': 'City', name: 'Franklin', containedInPlace: { '@type': 'State', name: 'Tennessee' } },
            { '@type': 'City', name: 'Brentwood', containedInPlace: { '@type': 'State', name: 'Tennessee' } },
            { '@type': 'City', name: 'Murfreesboro', containedInPlace: { '@type': 'State', name: 'Tennessee' } },
            { '@type': 'City', name: 'Hendersonville', containedInPlace: { '@type': 'State', name: 'Tennessee' } },
          ],
          serviceType: [
            'AI Web Development Nashville',
            'Website Design Nashville',
            'Website Hosting Nashville',
            'Website Management Nashville',
            'Small Business Website Nashville',
          ],
          openingHoursSpecification: [
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
              opens: '09:00',
              closes: '18:00',
            },
          ],
          offers: [
            {
              '@type': 'Offer',
              name: 'Website Build',
              price: '1000',
              priceCurrency: 'USD',
              description: 'Professional AI-powered website design and setup for Nashville small businesses',
            },
            {
              '@type': 'Offer',
              name: 'Basic Hosting Plan',
              price: '50',
              priceCurrency: 'USD',
              description: 'Website hosting, security, and backups for Nashville businesses',
            },
            {
              '@type': 'Offer',
              name: 'Managed Website Plan',
              price: '99',
              priceCurrency: 'USD',
              description: 'Hosting plus website updates and maintenance for Nashville businesses',
            },
          ],
        },
      },
      {
        id: 'home-webpage-schema',
        data: {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          '@id': `${siteUrl}/#webpage`,
          url: `${siteUrl}/`,
          name: 'Web Development Nashville TN | GWDevelopers AI Web Design',
          description:
            'Nashville web development agency using AI to build fast, professional websites for small businesses. Serving Nashville, Franklin, Brentwood & East Nashville.',
          isPartOf: { '@id': `${siteUrl}/#website` },
          about: { '@id': `${siteUrl}/#localbusiness` },
          dateModified: '2026-03-17',
          inLanguage: 'en-US',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: `${siteUrl}/`,
              },
            ],
          },
        },
      },
      {
        id: 'home-faq-schema',
        data: {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'Do you offer web development in Nashville, TN?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes! GWDevelopers is a web development agency serving Nashville, TN and surrounding areas including Franklin, Brentwood, East Nashville, and Murfreesboro. We build AI-powered websites for local small businesses.',
              },
            },
            {
              '@type': 'Question',
              name: 'How much does a website cost for a Nashville small business?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Our website build starts at $1,000 for a professional, custom-designed site — affordable for Nashville small businesses. Monthly hosting plans start at $50/month with no hidden fees.',
              },
            },
            {
              '@type': 'Question',
              name: 'What makes GWDevelopers different from other Nashville web design agencies?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'We use AI-powered development to deliver professional websites faster and more affordably than traditional Nashville web agencies. Most websites are live within days, not months.',
              },
            },
            {
              '@type': 'Question',
              name: 'Do you offer website hosting and management for Nashville businesses?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes. We offer a Basic Hosting Plan at $50/month and a Managed Website Plan at $99/month that includes unlimited content updates, SEO monitoring, and priority support — perfect for busy Nashville business owners.',
              },
            },
            {
              '@type': 'Question',
              name: 'How fast can I get a website for my Nashville business?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Most Nashville businesses have a live website within 1–2 weeks. Our AI-powered process means we can deliver a preview website in as little as 24 hours after your initial consultation.',
              },
            },
          ],
        },
      },
    ];

    schemas.forEach(({ id, data }) => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      script.text = JSON.stringify(data);
      document.head.appendChild(script);
    });

    return () => {
      schemas.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  return null;
};

export default HomeSEO;
